import { useCallback, useMemo } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { SponsorshipResponse } from '@/api/types/Sponsorship'
import dayjs, { Dayjs } from 'dayjs'

interface IndividualSponsorshipFormType {
  name: SponsorshipResponse['name']
  url: SponsorshipResponse['url']
  beginningDate: dayjs.Dayjs | null
  endDate?: dayjs.Dayjs | null
}

const useIndividualSponsorshipForm = (sponsorship: Partial<SponsorshipResponse> | null) => {
  const defaultValues = useCallback((i: Partial<SponsorshipResponse> | null): IndividualSponsorshipFormType => ({
    name: i?.name || '',
    url: i?.url || '',
    beginningDate: i?.beginningDate ? dayjs(i.beginningDate) : null,
    endDate: i?.endDate ? dayjs(i.endDate) : null,
  }), [])

  const validationSchema = useMemo(() => z.object({
    name: z.string().min(2, 'Name must be at least 2 characters'),
    url: z.url('Invalid URL'),
    beginningDate: z.custom<Dayjs | null>(val => val === null || val instanceof dayjs, 'Invalid date').nullable()
      .refine(val => val !== null, 'Beginning date is required'),
    endDate: z.custom<Dayjs | null>(val => val === null || val instanceof dayjs, 'Invalid date').nullable().nullish()
  }), [])

  const formDefaults = useMemo(() => defaultValues(sponsorship), [sponsorship, defaultValues])

  const form = useForm<IndividualSponsorshipFormType>({
    defaultValues: formDefaults,
    resolver: zodResolver(validationSchema),
  })

  const resetForm = useCallback((i: Partial<SponsorshipResponse> | null) => {
    form.reset(defaultValues(i))
  }, [form, defaultValues])

  return { form, defaultValues, resetForm }
}

export default useIndividualSponsorshipForm
